import { rate, type Rate } from '@radish/primitives';
import { err, ok, type Result } from '../result.js';

/**
 * Lossless conversion between legacy decimal strings and Rate (parts per 10^8). No floating point anywhere:
 * the string's digits become a bigint and are shifted by a power of ten.
 *
 * | form    | legacy source                                       | example           | Rate.scaled | shift |
 * |---------|-----------------------------------------------------|-------------------|-------------|-------|
 * | bp_x100 | locations.tax, tax_rate_histories.tax_rate (6,2)    | '882.50' = 8.825% | 8_825_000n  | 10^4  |
 * | bp      | locations.cash_discount_fee                         | '399' = 3.99%     | 3_990_000n  | 10^4  |
 * | percent | discount/preset/price-adjustment percents, gratuity | '12.5' = 12.5%    | 12_500_000n | 10^6  |
 *
 * `bp_x100` and `bp` share a shift: the tax column's value is hundredths of a percent, which is one basis point
 * per unit, the same unit as cash_discount_fee. They differ only in how many decimals the column prints.
 */
export type LegacyRateForm = 'bp_x100' | 'bp' | 'percent';

const SHIFT: Readonly<Record<LegacyRateForm, number>> = { bp_x100: 4, bp: 4, percent: 6 };
/** Decimal places the legacy column always prints (decimal(6,2) for tax; integers elsewhere). */
const MIN_DECIMALS: Readonly<Record<LegacyRateForm, number>> = { bp_x100: 2, bp: 0, percent: 0 };

const DECIMAL = /^(\d+)(?:\.(\d+))?$/;

/** Anything that is not a plain non-negative decimal, or has more decimals than the form can hold, is unrepresentable. */
const parse = (text: string, form: LegacyRateForm): Result<Rate, 'unrepresentable'> => {
  const match = DECIMAL.exec(text);
  if (!match) return err('unrepresentable');
  const whole = match[1] ?? '0';
  const fraction = (match[2] ?? '').replace(/0+$/, '');
  const shift = SHIFT[form];
  if (fraction.length > shift) return err('unrepresentable');
  return ok(rate(BigInt(whole + fraction.padEnd(shift, '0'))));
};

/** `locations.tax` / `tax_rate_histories.tax_rate`: '882.50' → 8_825_000n. */
export const rateFromLegacyTax = (taxBpTimes100: string): Result<Rate, 'unrepresentable'> =>
  parse(taxBpTimes100, 'bp_x100');

/** `locations.cash_discount_fee` in basis points: '399' → 3_990_000n. */
export const rateFromLegacyBp = (bp: string): Result<Rate, 'unrepresentable'> => parse(bp, 'bp');

/**
 * The integer legacy stored as `locations.cash_discount_fee`, back from its Rate: 990_000n → 99n. Legacy reads that one
 * integer as basis points or, for a fixed non-cash adjustment, as cents. Null when the Rate is not a whole number of
 * basis points (only a Rate set outside the import can be one).
 */
export const wholeLegacyBp = (r: Rate): bigint | null => (r.scaled % 10n ** BigInt(SHIFT.bp) === 0n ? r.scaled / 10n ** BigInt(SHIFT.bp) : null);

/** A percent: '12.5' → 12_500_000n. */
export const rateFromPercent = (pct: string): Result<Rate, 'unrepresentable'> => parse(pct, 'percent');

/**
 * The exact inverse of the parsers: formats a Rate in a legacy form with no loss. Trailing zeros are trimmed down
 * to the column's natural width ('882.50', '399', '12.5'), so a canonical legacy string round-trips byte for byte.
 * Negative rates get a leading '-' (the parsers never produce them).
 */
export const rateToLegacy = (r: Rate, form: LegacyRateForm): string => {
  const shift = SHIFT[form];
  const negative = r.scaled < 0n;
  const digits = (negative ? -r.scaled : r.scaled).toString().padStart(shift + 1, '0');
  const whole = digits.slice(0, digits.length - shift);
  const fraction = digits
    .slice(digits.length - shift)
    .replace(/0+$/, '')
    .padEnd(MIN_DECIMALS[form], '0');
  const sign = negative ? '-' : '';
  return fraction ? `${sign}${whole}.${fraction}` : `${sign}${whole}`;
};
